/**
 * Shape adapters between what the Setup tabs hold in state (the Phase-1
 * mock shapes, already in the UI's vocabulary) and what the webback API
 * stores. Pure functions, no imports — unit-tested with `node --test`.
 *
 * Direction naming: `toServer(client)` builds the request DTO,
 * `fromServer(row)` rebuilds the client object from a response.
 *
 * Money and deltas travel as **strings** on the wire (the server refuses
 * to emit floats for prices); the client keeps numbers for its inputs.
 */

const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v))
const str = (v) => (v === null || v === undefined ? null : String(v))

// ---------------------------------------------------------------------------
// Consistency groups — client: { id, name, items: [code] } per mode
// ---------------------------------------------------------------------------

export const consistency = {
	toServer: (g, mode) => ({
		name: g.name || 'Group',
		mode,
		notes: g.notes ?? null,
		items: (g.items || []).filter(Boolean),
	}),
	fromServer: (row) => ({
		id: row.id,
		mode: row.mode,
		name: row.name,
		notes: row.notes ?? null,
		items: (row.items || []).map(i => (typeof i === 'string' ? i : i.value_code)),
	}),
	/** What makes two client groups "the same content" for diffing. */
	fingerprint: (g, mode) => JSON.stringify(consistency.toServer(g, mode)),
}

// ---------------------------------------------------------------------------
// Price limits — client: scope_duration {unit,min,max} | null, numbers
// ---------------------------------------------------------------------------

export const priceLimits = {
	toServer: (r) => ({
		// Price Seeker ids, never slugs, and never `|| null`: `false` is
		// Non Prepaid and would be erased by a falsiness check.
		scope_ps_provider_id:  r.scope_ps_provider_id ?? null,
		scope_is_prepaid:      r.scope_is_prepaid ?? null,
		scope_ps_insurance_id: r.scope_ps_insurance_id ?? null,
		scope_office_group:   r.scope_office_group || null,
		// The single column is read for old rows and never written again.
		scope_office:         null,
		scope_offices:        r.scope_offices?.length ? [...r.scope_offices] : null,
		scope_categories:     r.scope_categories?.length
			? r.scope_categories.map(c => ({ type: c.type, code: c.code })) : null,
		// No unit any more: the engine never read it, so a rule saved
		// in weeks matched days. Everything is days (CU-869f9n466).
		scope_duration_unit:  r.scope_duration ? 'days' : null,
		scope_duration_min:   r.scope_duration ? num(r.scope_duration.min) : null,
		scope_duration_max:   r.scope_duration ? num(r.scope_duration.max) : null,
		date_from: r.date_from || null,
		date_to:   r.date_to || null,
		// Date protection (CU-869fe8jrk): the two dates above read as
		// "any rental that crosses them" and the floor composes by max.
		protects_dates: !!r.protects_dates,
		name: r.protects_dates ? (r.name?.trim() || null) : null,
		// A binding takes its floor from a matrix and carries no prices
		// of its own; the server refuses a row that tries to have both.
		matrix_id: r.matrix_id || null,
		min_price: r.matrix_id ? null : str(num(r.min_price)),
		max_price: r.matrix_id ? null : str(num(r.max_price)),
	}),
	fromServer: (row) => ({
		id: row.id,
		scope_ps_provider_id:  row.scope_ps_provider_id ?? null,
		scope_is_prepaid:      row.scope_is_prepaid ?? null,
		scope_ps_insurance_id: row.scope_ps_insurance_id ?? null,
		scope_office_group:   row.scope_office_group,
		scope_offices:        row.scope_offices?.length ? row.scope_offices
		                      : (row.scope_office ? [row.scope_office] : null),
		scope_categories:     row.scope_categories?.length ? row.scope_categories : null,
		// Keyed on the bounds, not on the unit: the unit is on its way
		// out of the table and a row that arrives without it still has
		// a duration scope (CU-869f9n466, phase 6). `unit` stays in the
		// object the screen reads, always 'days'.
		scope_duration: (row.scope_duration_min != null || row.scope_duration_max != null)
			? { unit: 'days', min: row.scope_duration_min, max: row.scope_duration_max }
			: null,
		date_from: row.date_from,
		date_to:   row.date_to,
		protects_dates: !!row.protects_dates,
		name: row.name || null,
		matrix_id: row.matrix_id || null,
		min_price: num(row.min_price),
		max_price: num(row.max_price),
		specificity: row.specificity,   // server-computed; the tab recomputes for ordering
	}),
	fingerprint: (r) => JSON.stringify(priceLimits.toServer(r)),
	/**
	 * Scope and dates, without the amounts — the server's own rule for
	 * what counts as two limits colliding. The full fingerprint tells
	 * "did this row change"; this one tells "is this row the same
	 * limit as that one", and they are different questions: a clone
	 * whose price has been edited is still a duplicate scope.
	 */
	scopeKey: (r) => {
		// `matrix_id` is part of the key on purpose: two bindings on one
		// scope are legitimate when their matrices price different cars
		// (one matrix per class, Thibault's way), so the client does not
		// hold them; whether two matrices overlap is the server's call,
		// and it names the ACRISS when they do (2026-10-02).
		const { min_price, max_price, ...scope } = priceLimits.toServer(r)
		return JSON.stringify(scope)
	},
	/**
	 * `scopeKey` without `name`: the server's own duplicate rule
	 * (`services/price_limits.py`: scope and dates collide, the name
	 * does not). The drawer's pre-check compares with this one, so two
	 * protections that differ only by name are caught before Save
	 * instead of by the 409 (CU-869fkbjdh, TL obs. 10). `readyToSave`
	 * keeps `scopeKey`.
	 */
	duplicateKey: (r) => {
		const { min_price, max_price, name, ...scope } = priceLimits.toServer(r)
		return JSON.stringify(scope)
	},
}

// ---------------------------------------------------------------------------
// Rate cards — client uses pricelist CODES and grouped derived writes;
// server uses pricelist IDS and a flat, ordered `writes` list.
// ---------------------------------------------------------------------------

/** `catalog` = [{id, code, name}] from GET /setup/pricelists. */
export function pricelistMaps(catalog) {
	const idByCode = new Map(), codeById = new Map()
	for (const p of catalog || []) { idByCode.set(p.code, p.id); codeById.set(p.id, p.code) }
	return { idByCode, codeById }
}

export const rateCards = {
	toServer: (f, maps) => {
		const id = (code) => {
			const v = maps.idByCode.get(code)
			if (!v) throw new UnknownPricelist(code)
			return v
		}
		const writes = []
		for (const code of f.write_primary || []) {
			if (code) writes.push({ kind: 'primary', pricelist_id: id(code), delta_kind: null, delta_value: null })
		}
		for (const d of f.write_derived || []) {
			for (const code of d.pricelists || []) {
				if (code) writes.push({ kind: 'derived', pricelist_id: id(code),
				                        delta_kind: d.delta?.k || 'pct', delta_value: str(num(d.delta?.v ?? 0)) })
			}
		}
		return {
			site_id: f.site_id == null ? null : Number(f.site_id),
			is_prepaid:      f.is_prepaid ?? null,
			ps_insurance_id: f.ps_insurance_id ?? null,
			ps_provider_id:  f.ps_provider_id ?? null,
			read_pricelist_id: f.read_pricelist ? id(f.read_pricelist) : null,
			markup_limit_pct: str(num(f.markup_limit)),
			writes,
			tiers: (f.tier_config || []).map(t => ({
				duration_from: t.duration_from, duration_to: t.duration_to, reference_lor: t.reference_lor })),
		}
	},
	fromServer: (row, maps) => {
		const code = (pid) => maps.codeById.get(pid) || pid
		const write_primary = []
		const derivedGroups = new Map()   // "kind|value" → { pricelists, delta }
		for (const w of row.writes || []) {
			if (w.kind === 'primary') { write_primary.push(code(w.pricelist_id)); continue }
			const key = `${w.delta_kind}|${w.delta_value}`
			if (!derivedGroups.has(key)) {
				derivedGroups.set(key, { pricelists: [], delta: { k: w.delta_kind, v: num(w.delta_value) } })
			}
			derivedGroups.get(key).pricelists.push(code(w.pricelist_id))
		}
		return {
			id: row.id,
			site_id: row.site_id == null ? null : Number(row.site_id),
			is_prepaid:      row.is_prepaid ?? null,
			ps_insurance_id: row.ps_insurance_id ?? null,
			ps_provider_id:  row.ps_provider_id ?? null,
			read_pricelist: row.read_pricelist_id ? code(row.read_pricelist_id) : null,
			write_primary,
			write_derived: [...derivedGroups.values()],
			markup_limit: num(row.markup_limit_pct),
			tier_config: (row.tiers || []).map(t => ({
				duration_from: t.duration_from, duration_to: t.duration_to, reference_lor: t.reference_lor })),
			tier_drift: row.tier_drift ?? false,
			canonical_tiers: row.canonical_tiers || [],
		}
	},
	fingerprint: (f, maps) => {
		try { return JSON.stringify(rateCards.toServer(f, maps)) } catch { return JSON.stringify(f) }
	},
}

export class UnknownPricelist extends Error {
	constructor(code) {
		super(`pricelist "${code}" is not in this tenant's catalogue`)
		this.name = 'UnknownPricelist'
		this.code = code
	}
}

// ---------------------------------------------------------------------------
// Tethers — client: { parent, children, delta:{k,v}, name, name_custom }
// ---------------------------------------------------------------------------

export const tethers = {
	toServer: (t) => ({
		dimension: t.dimension,
		parent_value: t.parent,
		children: (t.children || []).filter(Boolean),
		delta_kind: t.delta?.k || 'pct',
		delta_value: str(num(t.delta?.v ?? 0)),
		name: t.name_custom ? (t.name || null) : null,
		name_custom: !!t.name_custom,
	}),
	fromServer: (row) => ({
		id: row.id,
		dimension: row.dimension,
		parent: row.parent_value,
		children: (row.children || []).map(c => (typeof c === 'string' ? c : c.child_value)),
		delta: { k: row.delta_kind, v: num(row.delta_value) },
		name: row.name,
		name_custom: !!row.name_custom,
	}),
	fingerprint: (t) => JSON.stringify(tethers.toServer(t)),
}


// ---------------------------------------------------------------------------
// Price matrices — a floor template with no scope of its own.
//
// Bands are stored the way they are authored: `{from, to, …}` with
// `to: null` as the open tail ("30+", the monthly price). Not a
// 30-slot array — the tail has no upper edge and an array cannot say
// so, and a single-day override is simply a band of one day, which
// keeps one representation instead of two that can disagree.
// ---------------------------------------------------------------------------

const band = (b) => ({ from: num(b.from), to: b.to == null ? null : num(b.to) })

export const priceMatrices = {
	toServer: (m) => ({
		name: (m.name || '').trim(),
		base_acriss: m.base_acriss || '',
		grid: {
			base: (m.base || []).map(b => ({ ...band(b), price: num(b.price) })),
			rules: (m.rules || []).map(r => ({
				acriss: r.acriss,
				bands: (r.bands || []).map(b => ({
					...band(b), value: num(b.value), unit: b.unit || 'euro',
				})),
			})),
		},
	}),
	fromServer: (row) => ({
		id: row.id,
		name: row.name,
		base_acriss: row.base_acriss,
		base: (row.grid?.base || []).map(b => ({ ...band(b), price: num(b.price) })),
		rules: (row.grid?.rules || []).map(r => ({
			acriss: r.acriss,
			bands: (r.bands || []).map(b => ({
				...band(b), value: num(b.value), unit: b.unit || 'euro',
			})),
		})),
	}),
	fingerprint: (m) => JSON.stringify(priceMatrices.toServer(m)),
	/**
	 * The floor this matrix sets for one ACRISS at one length of
	 * rental, or `null` when it sets none.
	 *
	 * The same arithmetic the engine runs (`limits.floor_from_matrix`),
	 * duplicated here on purpose: the operator has to see the number
	 * before saving, and a preview that asks the server would make
	 * every keystroke a round trip. The two are pinned together by
	 * test — if they ever disagree, the screen is lying.
	 */
	floorFor: (m, acriss, lor) => {
		const covers = (b) => (b.from == null || lor >= b.from)
		                   && (b.to == null || lor <= b.to)
		const baseBand = (m.base || []).find(covers)
		if (!baseBand || baseBand.price == null) return null
		if (acriss === m.base_acriss) return round2(baseBand.price)

		const rule = (m.rules || []).find(r => r.acriss === acriss)
		if (!rule) return null
		const b = (rule.bands || []).find(covers)
		if (!b || b.value == null) return null
		return round2(b.unit === 'percent'
			? baseBand.price * (1 + b.value / 100)
			: baseBand.price + b.value)
	},
}

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100


// ---------------------------------------------------------------------------
// Products (CU-869fhuhy6) — client: { id, name, notes, renameHint, usedBy,
//   rows: [{ id, siteId, tariffType, insuranceId, readPricelistId,
//            writePricelistId, capPct, writeDelta: {k, v} | null,
//            derived: [{ id, pricelistId, delta: {k, v} }],
//            tierConfig: [{ duration_from, duration_to, reference_lor }] }] }
//
// The server row is `ProductIn` (webback `schemas.py`): snake_case, ids
// as strings, decimals as strings, `tiers: null` meaning "leave them".
// Pricelists travel as ids on both sides — unlike the rate cards, the
// card never worked with codes, so no code ↔ id map is needed here.
// ---------------------------------------------------------------------------

const writeDeltaToServer = (d) => {
	const v = num(d?.v)
	if (v == null || !Number.isFinite(v) || v === 0) return { write_delta_kind: null, write_delta_value: null }
	return { write_delta_kind: d.k === 'eur' ? 'abs' : 'pct', write_delta_value: str(v) }
}

export const isClientId = (id) => id == null || /^(tmp|p|new)_/.test(String(id))

export const products = {
	toServer: (p) => ({
		name: String(p.name || '').trim(),
		notes: p.notes ?? null,
		rows: (p.rows || []).map(r => ({
			// The server excludes the row being edited from its own rules
			// by id; a row the server has never seen carries none.
			...(isClientId(r.id) ? {} : { id: r.id }),
			site_id: r.siteId == null ? null : Number(r.siteId),
			is_prepaid: r.tariffType == null ? null : !!r.tariffType,
			ps_insurance_id: r.insuranceId == null ? null : Number(r.insuranceId),
			read_pricelist_id: str(r.readPricelistId),
			write_pricelist_id: str(r.writePricelistId),
			markup_limit_pct: str(num(r.capPct)),
			// The write's delta (CU-869fkr1a9): both or neither; no value (or 0) is no delta.
			...writeDeltaToServer(r.writeDelta),
			derived: (r.derived || []).filter(d => d.pricelistId != null).map(d => ({
				pricelist_id: str(d.pricelistId),
				delta_kind: d.delta?.k === 'eur' ? 'abs' : 'pct',
				delta_value: str(num(d.delta?.v ?? 0)),
			})),
			// `null` = leave the tiers as they are; `[]` = drop them. The
			// card only sends what the editor produced (`tiersToSave`).
			tiers: Array.isArray(r.tierConfig) && r.tierConfig.length
				? r.tierConfig.map(t => ({ duration_from: t.duration_from, duration_to: t.duration_to, reference_lor: t.reference_lor }))
				: null,
		})),
	}),
	fromServer: (row) => ({
		id: row.id,
		name: row.name || '',
		notes: row.notes ?? null,
		// The migration's provisional name (criterion 33, UX v3 D16-f):
		// the chip stays until the human writes another name.
		renameHint: !!row.rename_me,
		usedBy: Array.isArray(row.used_by) ? row.used_by : [],
		rows: (row.rows || []).map(r => ({
			id: r.id,
			siteId: r.site_id == null ? null : Number(r.site_id),
			tariffType: r.is_prepaid == null ? null : !!Number(r.is_prepaid),
			insuranceId: r.ps_insurance_id == null ? null : Number(r.ps_insurance_id),
			// Pricelist ids are UUID strings: `Number(uuid)` is NaN, and NaN is not
			// nullish, so `num(id) ?? id` kept NaN and the saved row read as "no
			// read pricelist yet" (Curro, 2026-10-20). Ids stay strings end to end.
			readPricelistId: str(r.read_pricelist_id),
			writePricelistId: str(r.write_pricelist_id),
			capPct: num(r.markup_limit_pct),
			writeDelta: r.write_delta_kind && num(r.write_delta_value) != null
				? { k: r.write_delta_kind === 'abs' ? 'eur' : 'pct', v: num(r.write_delta_value) }
				: null,
			derived: (r.derived || []).map(d => ({
				id: `${r.id}:${d.pricelist_id}`,
				pricelistId: str(d.pricelist_id),
				delta: { k: d.delta_kind === 'abs' ? 'eur' : 'pct', v: num(d.delta_value) ?? 0 },
			})),
			tierConfig: (r.tiers || []).map(t => ({
				duration_from: t.duration_from, duration_to: t.duration_to, reference_lor: t.reference_lor })),
		})),
	}),
}
